import { Card } from '@components/card';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { InfoBox } from '@components/info-box';
import { RadioCardGroup } from '@components/radio-card';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUpdatePortalSecurity } from 'api/portals/hooks';
import { PortalDetail, PortalSecuritySettings, SSOPrefillFieldMode } from 'api/portals/requests';

export type PortalAzureSSOAdvancedCardProps = {
  portal: PortalDetail;
  securitySettings: PortalSecuritySettings;
};

export const PortalAzureSSOAdvancedCard = ({
  portal,
  securitySettings,
}: PortalAzureSSOAdvancedCardProps) => {
  const { t } = useScopedTranslate(locale);
  const updateSecurityMutation = useUpdatePortalSecurity(portal.shortId);

  const handleModeChange = (value: string | number) => {
    updateSecurityMutation.mutate({
      ssoPrefillMode: value as SSOPrefillFieldMode,
      azureTenantId: securitySettings.azureTenantId ?? '',
      azureClientId: securitySettings.azureClientId,
      azureAuthorizedGroupId: securitySettings.azureAuthorizedGroupId ?? '',
    });
  };

  return (
    <Card space={6} variant="transparent">
      <Stack space={4}>
        <Stack space={2}>
          <Text text={t('ssoIdentityTitle')} font="bold18" />
          <Text text={t('ssoIdentityDescription')} font="regular14" color="textSecondary" />
        </Stack>
        <RadioCardGroup
          label={t('ssoIdentityRadioLabel')}
          value={securitySettings.ssoPrefillMode}
          onChange={handleModeChange}
        >
          <RadioCardGroup.Option
            value="do_not_use"
            label={t('ssoOptionDoNotUse')}
            helpText={t('ssoOptionDoNotUseHelp')}
          />
          <RadioCardGroup.Option
            value="prefill_only"
            label={t('ssoOptionPrefillOnly')}
            helpText={t('ssoOptionPrefillOnlyHelp')}
          />
          <RadioCardGroup.Option
            value="prefill_and_lock"
            label={t('ssoOptionPrefillAndLock')}
            helpText={t('ssoOptionPrefillAndLockHelp')}
          />
        </RadioCardGroup>
        <InfoBox variant="indigo">
          <Stack orientation="horizontal" align="top" space={2}>
            <IconFormIndicatorInfo color="indigo100" />
            <Text font="regular14" text={t('ssoIdentityOverrideInfo')} />
          </Stack>
        </InfoBox>
      </Stack>
    </Card>
  );
};

const locale = {
  en: {
    ssoIdentityTitle: 'Azure settings (Advanced)',
    ssoIdentityDescription:
      'Automatically map the signed-in portal users identity to submitter fields. This is default for all forms in this portal.',
    ssoIdentityRadioLabel: 'Default field mapping',
    ssoOptionPrefillOnly: 'Pre-fill only',
    ssoOptionPrefillOnlyHelp: 'Pre-fill Full name and Email but let the user edit them',
    ssoOptionPrefillAndLock: 'Pre-fill and lock',
    ssoOptionPrefillAndLockHelp: 'Name and email are pre-filled and can not be edited',
    ssoOptionDoNotUse: 'Do not use portal identity',
    ssoOptionDoNotUseHelp: 'Users enters name and email manually',
    ssoIdentityOverrideInfo: 'Individual forms can override these settings',
  },
};
